/**
 * OutreachApprovalsPage — S59 Phase 2 (§3.7).
 *
 * The Founder's review inbox for OMEGA-composed outreach emails. Every email is
 * queued `pending` and SENDS NOTHING until the Founder approves it here. Approve
 * → the email is sent (when a Gmail send credential is provisioned) and the
 * receipt recorded; Reject → marked rejected, never sent.
 *
 * One-click approve/reject: the page computes the JCS-canonical hash of the
 * request body and signs it with the founder warm key (`/admin/founder/sign-hash`),
 * exactly like the Finance approval flow — no manual signature paste.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

/// Anti-injection phrase required on Founder-gated mutating endpoints (require_founder).
const OMEGA_CONFIRM = 'GI-OMEGA-SHALEV-2026';

// ── Types (snake_case mirrors the Rust wire shape) ────────────────────────────

interface PendingItem {
  entry_id: string;
  lead_id: string;
  recipient: string | null;
  subject: string;
  body: string;
  follow_up_number: number;
  queued_at: number;
}
interface PendingResponse {
  items: PendingItem[];
  total: number;
}
interface HistoryItem {
  item_id: string;
  item_type: string;
  lead_id: string;
  subject: string;
  status: string;
  queued_at: number;
  decided_at: number | null;
  rejection_reason: string | null;
}
interface HistoryResponse {
  items: HistoryItem[];
  total: number;
}
interface ApproveResponse {
  entry_id: string;
  action: string;
  sent: boolean;
  provider_message_id: string | null;
  thread_id: string | null;
  reason: string;
}

// ── Data ──────────────────────────────────────────────────────────────────────

const fetchPending = () => apiFetch<PendingResponse>('/admin/outreach/pending');

// History reuses the unified approval-history endpoint, filtered to outreach.
const fetchHistory = () =>
  apiFetch<HistoryResponse>('/sales/approvals/history?type=outreach&days=30');

async function approveEntry(entryId: string): Promise<ApproveResponse> {
  const body = { nonce: crypto.randomUUID() };
  return apiFetch<ApproveResponse>(`/admin/outreach/${encodeURIComponent(entryId)}/approve`, {
    method: 'POST',
    body,
    headers: { 'X-Omega-Confirm': OMEGA_CONFIRM },
  });
}

async function rejectEntry(entryId: string, reason: string): Promise<void> {
  const body = { reason, nonce: crypto.randomUUID() };
  await apiFetch(`/admin/outreach/${encodeURIComponent(entryId)}/reject`, {
    method: 'POST',
    body,
    headers: { 'X-Omega-Confirm': OMEGA_CONFIRM },
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function touchLabel(n: number): string {
  if (n <= 0) return 'Cold email #1';
  return `Follow-up #${n + 1}`;
}

function fmtTime(secs: number | null): string {
  if (!secs) return '—';
  return new Date(secs * 1000).toLocaleString();
}

function statusColor(status: string): string {
  switch (status) {
    case 'sent':
    case 'approved':
      return '#34d399';
    case 'rejected':
      return '#f87171';
    default:
      return '#fbbf24';
  }
}

// ── Styles ────────────────────────────────────────────────────────────────────

const btn = (color: string, disabled = false): React.CSSProperties => ({
  background: `rgba(${hexToRgb(color)},0.12)`,
  border: `1px solid rgba(${hexToRgb(color)},0.4)`,
  borderRadius: 4,
  color,
  fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt',
  fontWeight: 600,
  padding: '6px 14px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  letterSpacing: '0.04em',
});

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4,
  color: 'var(--omega-ink)',
  fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt',
  padding: '4px 8px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: ok ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
        border: `1px solid ${ok ? '#34d399' : '#f87171'}`,
        borderRadius: 6,
        color: ok ? '#34d399' : '#f87171',
        fontFamily: 'var(--omega-mono)',
        fontSize: '9pt',
        padding: '10px 20px',
        zIndex: 9999,
        direction: 'ltr',
        maxWidth: 520,
      }}
    >
      {msg}
    </div>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────────--

function PendingRow({
  item,
  onApprove,
  onReject,
  busy,
}: {
  item: PendingItem;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const preview = item.body.length > 240 && !expanded ? `${item.body.slice(0, 240)}…` : item.body;

  return (
    <div
      style={{
        border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
        borderRadius: 6,
        padding: '14px 16px',
        marginBottom: 12,
        background: 'rgba(255,255,255,0.02)',
        direction: 'ltr',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: '10pt' }}>{item.subject}</span>
        <span
          style={{
            fontSize: '7.5pt',
            padding: '2px 8px',
            border: '1px solid #fbbf24',
            color: '#fbbf24',
            borderRadius: 3,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          {touchLabel(item.follow_up_number)}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 20, marginTop: 6, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', flexWrap: 'wrap' }}>
        <span>To: <b style={{ color: 'var(--omega-ink)' }}>{item.recipient ?? '— no recipient —'}</b></span>
        <span>Lead: {item.lead_id}</span>
        <span>Queued: {fmtTime(item.queued_at)}</span>
      </div>

      <div
        onClick={() => setExpanded((e) => !e)}
        style={{
          background: '#fff',
          color: '#111',
          borderRadius: 5,
          padding: '12px 16px',
          marginTop: 10,
          fontSize: '9.5pt',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          fontFamily: 'Georgia, serif',
          cursor: 'pointer',
        }}
        title={expanded ? 'Click to collapse' : 'Click to expand'}
      >
        {preview}
      </div>

      {!rejecting ? (
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button style={btn('#34d399', busy)} disabled={busy} onClick={() => onApprove(item.entry_id)}>
            {busy ? 'Working…' : 'Approve & Send'}
          </button>
          <button style={btn('#f87171', busy)} disabled={busy} onClick={() => setRejecting(true)}>
            Reject
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            style={inputStyle}
            placeholder="Reason for rejection…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
          <button
            style={btn('#f87171', busy || !reason.trim())}
            disabled={busy || !reason.trim()}
            onClick={() => onReject(item.entry_id, reason.trim())}
          >
            Confirm reject
          </button>
          <button style={btn('#9ca3af', busy)} disabled={busy} onClick={() => setRejecting(false)}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function OutreachApprovalsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }

  const pending = useQuery({
    queryKey: ['outreach', 'pending'],
    queryFn: fetchPending,
    refetchInterval: 30_000,
  });
  const history = useQuery({
    queryKey: ['outreach', 'history'],
    queryFn: fetchHistory,
    refetchInterval: 60_000,
  });

  // Force an immediate re-fetch of BOTH lists so an approved/rejected email moves
  // from Pending → History without a page refresh. `refetchQueries` (not just
  // invalidate) guarantees the network call fires now, regardless of staleness.
  const refresh = async () => {
    await queryClient.refetchQueries({ queryKey: ['outreach'], type: 'active' });
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveEntry(id),
    onSuccess: async (res) => {
      showToast(res.sent ? `Sent — message ${res.provider_message_id ?? ''}` : res.reason, res.sent);
      await refresh();
    },
    onError: () => showToast('Approve failed — check founder session and try again', false),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectEntry(id, reason),
    onSuccess: async () => {
      showToast('Email rejected — not sent', true);
      await refresh();
    },
    onError: () => showToast('Reject failed — try again', false),
  });

  const busy = approveMutation.isPending || rejectMutation.isPending;
  const items = pending.data?.items ?? [];
  const hist = history.data?.items ?? [];

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Outreach approvals' }]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 18px' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em' }}>Outreach approvals</h1>
        <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
          OMEGA composes — nothing sends until you approve.
        </span>
        <button style={{ ...btn('#7dd3fc'), marginLeft: 'auto' }} onClick={refresh}>
          Refresh
        </button>
      </div>

      {/* ── Pending ── */}
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
        Pending approval ({items.length})
      </div>
      {pending.isPending && <div style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)' }}>Loading…</div>}
      {pending.isError && <div style={{ fontSize: '9pt', color: '#f87171' }}>Failed to load pending emails.</div>}
      {!pending.isPending && items.length === 0 && (
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)' }}>No emails awaiting approval.</div>
      )}
      {items.map((it) => (
        <PendingRow
          key={it.entry_id}
          item={it}
          busy={busy}
          onApprove={(id) => approveMutation.mutate(id)}
          onReject={(id, reason) => rejectMutation.mutate({ id, reason })}
        />
      ))}

      {/* ── History ── */}
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '26px 0 10px' }}>
        History — sent &amp; rejected ({hist.length})
      </div>
      {hist.length === 0 ? (
        <div style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)' }}>No decided emails yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5pt', direction: 'ltr' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--omega-ink-subtle)' }}>
              <th style={{ padding: '6px 8px' }}>Subject</th>
              <th style={{ padding: '6px 8px' }}>Lead</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px' }}>Decided</th>
              <th style={{ padding: '6px 8px' }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {hist.map((h) => (
              <tr key={h.item_id} style={{ borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.08))' }}>
                <td style={{ padding: '6px 8px' }}>{h.subject}</td>
                <td style={{ padding: '6px 8px' }}>{h.lead_id}</td>
                <td style={{ padding: '6px 8px', color: statusColor(h.status), fontWeight: 700, textTransform: 'uppercase' }}>
                  {h.status}
                </td>
                <td style={{ padding: '6px 8px' }}>{fmtTime(h.decided_at)}</td>
                <td style={{ padding: '6px 8px', color: 'var(--omega-ink-subtle)' }}>{h.rejection_reason ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </div>
  );
}
